import type { Page } from "@inertiajs/core"
import { createInertiaApp, router } from "@inertiajs/react"
import { type ReactNode, createElement } from "react"
import { createRoot } from "react-dom/client"

import { initializeTheme } from "@/hooks/use-appearance"
import PersistentLayout from "@/layouts/persistent-layout"
import { loadPage } from "@/lib/load-page"
import { type TitleBrandingProps, formatPageTitle } from "@/lib/title-branding"

// Temporary type definition, until @inertiajs/react provides one
interface ResolvedComponent {
  default: ReactNode & { layout?: (page: ReactNode) => ReactNode }
}

// `||` not `??`: VITE_APP_NAME is baked as "" when unset at build time, and an
// empty string must fall back to the default (nullish coalescing wouldn't).
// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty-string
const appName = (import.meta.env.VITE_APP_NAME || "Growbotik") as string

let titleBrandingProps: TitleBrandingProps = {}

function setTitleBrandingProps(page: Page) {
  titleBrandingProps = page.props as TitleBrandingProps
}

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => formatPageTitle(title, titleBrandingProps, appName),

  resolve: async (name) => {
    // Lazy, not eager: each page is its own chunk, so a visitor downloads only
    // the page they open. Eager put all pages in one 5 MB entry that a mobile
    // visitor to /prospecting had to fetch before anything rendered.
    //
    // The negative patterns keep colocated `*.test.tsx` page tests, and the
    // `vitest` they import, out of the production build. With the old eager
    // glob that import ran at startup and took the whole app down; lazily it
    // would only be dead weight, but it has no place in the build.
    const pageLoaders = import.meta.glob<ResolvedComponent>([
      "../pages/**/*.tsx",
      "!../pages/**/*.test.tsx",
      "!../pages/**/*.spec.tsx",
    ])
    const importPage = pageLoaders[`../pages/${name}.tsx`]
    if (!importPage) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }
    const page = await loadPage(importPage)

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    page.default.layout ??= (page) =>
      createElement(PersistentLayout, null, page)

    return page
  },

  setup({ el, App, props }) {
    setTitleBrandingProps(props.initialPage)
    // Uncomment the following to enable SSR hydration:
    // if (el.hasChildNodes()) {
    //   hydrateRoot(el, createElement(App, props))
    //   return
    // }
    createRoot(el).render(createElement(App, props))
  },

  progress: false,
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})

// This will set light / dark mode on load...
initializeTheme()

router.on("invalid", (event) => {
  event.preventDefault()
  window.location.assign("/500")
})

router.on("beforeUpdate", (event) => {
  setTitleBrandingProps(event.detail.page)
})
